/*
 * Shared cart surgery for a built plan.
 *
 * Load with `defer` BEFORE any script that calls it (component-journey-resume.js,
 * section-plan-builder.js). Deferred scripts execute in document order, so the order
 * of the <script> tags is the dependency order.
 *
 * Usage:
 *   await window.PlanCart.clearPlanLines();
 *
 * A finished plan lives in the cart as Recharge bundle lines — one line per meal, plus
 * the parent box lines. They are the durable record of the plan: the `selections`
 * attribute is deleted as soon as the bundle is added (see the comment on
 * [data-pb-cart-plan] in sections/plan-builder.liquid), so cartDerivedSelections() in
 * section-plan-builder.js rebuilds the whole plan from these lines instead.
 *
 * Which is why clearing the ATTRIBUTES is not enough to start over. Restarting used to
 * call clearedConfigAttributes() and stop there, leaving the old meals in the cart for
 * the builder to faithfully restore — the customer clicked "Start a new plan" and got
 * the old one back. Removing the lines is the other half of that promise.
 *
 * A plan can also come back with its line properties STRIPPED: order 1085 carried a
 * previous order's boxes and meals as bare lines (re-added from outside the theme —
 * a "buy again" style copy), which no tag check could see. They survived "Start a new
 * plan" and checkout, showed in the cart review as Extras, and were charged as
 * one-offs. A plan box product with no _cr_plan_parent tag is never something the
 * builder makes, so it marks such a copy; the box ids come from
 * snippets/plan-box-products.liquid.
 */
if (!window.PlanCart) {
  // _rc_bundle / _rb_id are the meal lines; _cr_plan_parent the box that holds
  // them. A nested bundle has both kinds, so all three are checked.
  const isTagged = (item) => {
    const props = (item && item.properties) || {};
    return Boolean(props._rc_bundle || props._rb_id || props._cr_plan_parent);
  };

  // True when the cart holds a plan box the builder did not put there.
  const hasCopiedPlan = (items, boxProductIds) => {
    const boxes = new Set((boxProductIds || []).map(String));
    return (items || []).some((item) => boxes.has(String(item.product_id)) && !isTagged(item));
  };

  // { lineKey: 0 } for every line belonging to a plan. Tagged lines always; untagged
  // lines only alongside a copied box, so a standalone item bought from a product
  // page is left alone when there is no copied plan in the cart.
  const planLineUpdates = (items, boxProductIds) => {
    const copied = hasCopiedPlan(items, boxProductIds);
    const updates = {};
    (items || []).forEach((item) => {
      if (isTagged(item) || copied) updates[item.key] = 0;
    });
    return updates;
  };

  const readBoxProductIds = () => {
    const node = typeof document !== "undefined" && document.querySelector("[data-plan-box-products]");
    if (!node) return [];
    try {
      return JSON.parse(node.textContent.trim()) || [];
    } catch (e) {
      console.error("[plan-cart] failed to parse plan box products", e);
      return [];
    }
  };

  window.PlanCart = {
    planLineUpdates,
    hasCopiedPlan,

    // Zero out every line belonging to a plan. Resolves once the cart has been written,
    // so callers can await it before anything else touches the cart.
    //
    // Resolves quietly on a failed read rather than rejecting: the caller's next step is
    // always "let the customer get on with building a plan", and a cart that could not be
    // read is not a reason to block them at a dialog. A failed WRITE is logged, since
    // it leaves the old plan in the cart to ride into checkout.
    async clearPlanLines() {
      const res = await fetch(`${window.routes.cart_url}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return;
      const cart = await res.json();

      const updates = planLineUpdates(cart.items, readBoxProductIds());
      if (Object.keys(updates).length === 0) return;

      const updateUrl = window.routes.cart_update_url || "/cart/update";
      const write = await fetch(`${updateUrl}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ updates }),
      });
      if (!write.ok) console.error("[plan-cart] failed to clear the previous plan", write.status);
    },
  };
}
